import { formatDate } from "./format";
import { COVER_REFUND_MISMATCH_SENTENCE } from "./opsLabels";

// Service error code to one plain sentence. The OMS sends the code in `code`; its own `message`
// is only a fallback, because it may be a technical text.
export const DEFAULT_ERROR_MESSAGE =
  "Something went wrong. Try again, and tell engineering if it keeps happening.";

const MESSAGES = {
  PLAN_DRAFT_NOT_FOUND: "This draft no longer exists. Go back to the plans and open it again.",
  PLAN_DRAFT_EXISTS:
    "There is already an open draft for this tier. Continue that one, or discard it first.",
  PLAN_DRAFT_NOT_OPEN: "This draft was already published or discarded, so it cannot be changed.",
  PLAN_DRAFT_REVISION_CONFLICT:
    "This draft was changed elsewhere. Reload to see the latest version.",
  PLAN_DRAFT_INVALID: "The draft still has errors. Fix them, then publish again.",
  PLAN_DRAFT_STALE:
    "The live version changed after this draft was started. Start again from the live version.",
  PLAN_VERSION_DATE_TAKEN:
    "A version of this tier already starts on that date. Choose another date.",
  PLAN_SCHEDULE_AFTER:
    "Another version of this tier is scheduled to start after that date. Cancel it first, or choose a later date.",
  PLAN_EFFECTIVE_DATE_PAST: "The start date cannot be in the past.",
  PLAN_VERSION_NOT_FOUND: "This plan version no longer exists.",
  PLAN_VERSION_NOT_SCHEDULED:
    "This version has already started or its schedule was cancelled, so there is nothing to cancel.",
  PLAN_TIER_NOT_FOUND: "This tier has no live version to start from.",
  PLAN_SOURCE_VERSION_MISMATCH: "That version belongs to a different tier.",
  SERVICE_UNAVAILABLE: "The warranty service is not available right now. Try again in a minute.",
  MARKET_DISABLED: "The warranty service is not available right now. Try again in a minute.",
  RATE_LIMITED: "Too many requests. Wait a moment and try again.",
  // The OMS staff read budget (All warranties, journey).
  TOO_MANY_REQUESTS: "Too many requests. Wait a moment and try again.",
  PERMISSION_DENIED: "You do not have permission to do this. Ask an admin for the matching Warranty permission.",
  VALIDATION_FAILED: "Some of the values were not accepted. Check the highlighted fields.",
  ACTOR_REQUIRED: "Your staff account could not be identified. Log out and log in again.",
  ACTOR_INVALID: "Your staff account could not be identified. Log out and log in again.",
  CONTRACT_VERSION_MISMATCH:
    "This screen is out of date with the warranty service. Refresh the page and try again.",
  NETWORK_ERROR: "Could not reach the server. Check your connection and try again.",
  UNAUTHENTICATED: "Your session has expired. Log in again.",
  // Phase 2: review queue and the purchase date decision
  DATE_DECISION_REQUIRED:
    "The rider typed this purchase date. Choose to confirm it, correct it, or leave it unverified before approving.",
  DATE_DECISION_NOT_APPLICABLE:
    "A date decision does not apply to this registration any more. Reload it and decide again.",
  REVIEW_DATE_CHANGED:
    "The purchase date of this registration changed while you were reviewing. Reload it and decide again.",
  DATE_INVALID:
    "That purchase date is not valid. It must be a real date, not before 1 January 2019 and not after today.",
  PURCHASE_DATE_REQUIRED:
    "This registration has no purchase date. Enter the date on the invoice before approving.",
  PURCHASE_DATE_REASON_REQUIRED:
    "This registration already has a verified purchase date. Say why it is wrong to replace it.",
  PURCHASE_DATE_NOT_APPLICABLE: "A purchase date is only sent with an approval. Remove it to reject.",
  PURCHASE_DATE_LOCKED_BY_PLAN:
    "A paid plan was bought on this purchase date, so it cannot be changed here. Refund or end the plan first, or ask engineering.",
  REGISTRATION_NOT_FOUND: "This registration no longer exists. Reload the queue.",
  REGISTRATION_NOT_PENDING: "This registration was already decided. Reload the queue.",
  REGISTRATION_VERIFYING:
    "This registration is being checked automatically right now. Try again in a few minutes.",
  PRODUCT_REQUIRED:
    "This registration needs a product before it can be approved. Choose the product and approve again.",
  PRODUCT_NOT_FOUND:
    "The service does not know that product. Choose another one, or approve it from the OMS once a product is linked.",
  POSTCODE_UNKNOWN: "The rider's postcode is not known to the service. Ask engineering.",
  CLAIM_NOT_FOUND: "This ownership claim no longer exists. Reload the queue.",
  CLAIM_OWNER_CHANGED: "The claimant changed while you were reviewing. Reload the queue and decide again.",
  CLAIM_ALREADY_RESOLVED: "This ownership claim was already decided. Reload the queue.",
  DOCUMENT_NOT_FOUND: "This document no longer exists.",
  DOCUMENT_NOT_READY: "This document is still being processed. Try again in a minute.",
  DOCUMENT_OBJECT_CHANGED: "This document changed after it was uploaded, so it cannot be shown.",
  UPSTREAM_UNAVAILABLE: "The document store is not available right now. Try again in a minute.",
  // Phase 2: support
  PHONE_INVALID: "That phone number is not valid. Include the country code, for example +91.",
  SUBSCRIPTION_NOT_FOUND: "No subscription with this id exists.",
  PAYMENT_NOT_FOUND: "This payment no longer exists.",
  ENTITLEMENT_NOT_FOUND: "This benefit no longer exists.",
  RECEIPT_NOT_READY: "The receipt is not ready yet. Try again in a minute.",
  // Phase 2: exception actions
  OUTBOX_JOB_NOT_FOUND: "This job no longer exists. Reload the list.",
  OUTBOX_JOB_NOT_DEAD: "This job is no longer stopped, so there is nothing to retry. Reload the list.",
  OUTBOX_KIND_NOT_RETRYABLE: "Not safe to retry from here: see the outbox runbook.",
  PAYMENT_NOT_UNCONFIRMED: "This payment is no longer waiting for confirmation. Reload the list.",
  REFUND_NOT_FAILED: "This refund has not failed, or the provider already has it. Reload the list.",
  // Phase 2: refunds
  REFUND_NOT_AVAILABLE: "A refund is not available for this subscription.",
  REFUND_WINDOW_CLOSED: "The cooling-off window for this subscription has closed.",
  REFUND_BLOCKED_BY_USE: "The rider has already used a benefit, so the cooling-off refund is not allowed.",
  REFUND_BLOCKED_BY_POLICY:
    "This plan cannot be refunded in cooling-off: a partner has already issued a theft or roadside policy.",
  COUPON_ISSUER_UNAVAILABLE:
    "The coupon system could not be reached, so the refund was not made. Try again in a few minutes.",
  COUPON_ISSUER_REJECTED:
    "The coupon system refused to cancel the coupon, so the refund was not made. Ask engineering.",
  ENTITLEMENT_NOT_PENDING_ACTIVATION: "This benefit is no longer waiting to be activated.",
  // Phase 2: partner policies
  PARTNER_POLICY_NOT_FOUND: "This partner policy no longer exists. Reload the list.",
  PARTNER_POLICY_NOT_RETRYABLE:
    "This request cannot be retried now: it has moved on. Reload the list.",
  PARTNER_POLICY_NOT_AWAITING:
    "The insurer is not being waited on for this policy any more. Reload the list.",
  PARTNER_NOT_CONFIGURED:
    "No insurance partner is set up for this market and cover yet, so the request cannot be sent.",
  COVER_NOT_REFUNDABLE: "This cover cannot be refunded now: it is active, used, stopped or already refunded.",
  COVER_REFUND_IN_FLIGHT: "A cover refund is already in progress for this payment.",
  // Settle of a cover refund the provider answered with a different amount
  COVER_REFUND_NOT_MISMATCH:
    "This cover refund is no longer waiting to be settled: someone settled it, or it changed. Reload the list.",
  COVER_REFUND_AMOUNT_INVALID:
    "That amount was not accepted. Give the amount the provider refunded, no more than the rider paid on this payment, or choose that nothing was refunded.",
  // Theft & roadside vendor policy (contract 15 Oct 2026)
  VENDOR_POLICY_ALREADY_ISSUED: "A vendor policy was already added for this subscription.",
  VENDOR_POLICY_NOT_FOUND: "No vendor policy has been added for this subscription yet.",
  // Monthly partner settlement
  SETTLEMENT_TOO_LARGE:
    "This month has too many lines to download as one file. Ask engineering for the file.",
  // The OMS's own answer while warranty money actions are switched off
  MONEY_ACTIONS_DISABLED:
    "Warranty refunds are switched off for now. Ask the engineering team to switch them on.",
  NOT_FOUND: "This part of warranty admin is not switched on yet.",
  // A 404 with no code (the web framework's own page, for an id that is not a UUID).
  NOT_FOUND_ITEM: "We could not find that. The link may be wrong, or the item was removed.",
};

const dateLabel = (value) =>
  typeof value === "string" && /^\d{4}-\d{2}-\d{2}/.test(value) ? formatDate(value.slice(0, 10)) : null;

// `details` is the OMS's allowed detail object; it only refines a few sentences.
export const messageForCode = (code, httpStatus, details) => {
  if (typeof code === "string") {
    if (code === "REFUND_WINDOW_CLOSED" && dateLabel(details?.windowEndedOn)) {
      return `The cooling-off window for this subscription closed on ${dateLabel(details.windowEndedOn)}.`;
    }
    if (code === "COVER_REFUND_IN_FLIGHT" && details?.lineRefundStatus === "mismatch") {
      return COVER_REFUND_MISMATCH_SENTENCE;
    }
    if (code === "RATE_LIMITED" && Number.isInteger(details?.retryAfterSeconds)) {
      return `Too many requests. Wait ${details.retryAfterSeconds} seconds and try again.`;
    }
    if (MESSAGES[code] !== undefined) {
      return MESSAGES[code];
    }
    if (code.startsWith("IDEMPOTENCY")) {
      return "That request is still being processed or was reused. Wait a moment, then try again.";
    }
  }
  if (httpStatus === undefined || httpStatus === null) {
    return MESSAGES.NETWORK_ERROR;
  }
  if (httpStatus === 401) {
    return MESSAGES.UNAUTHENTICATED;
  }
  if (httpStatus === 404) {
    return MESSAGES.NOT_FOUND_ITEM;
  }
  if (httpStatus === 403) {
    return MESSAGES.PERMISSION_DENIED;
  }
  return DEFAULT_ERROR_MESSAGE;
};

// Codes that mean the draft or version is gone or finished, so a retry cannot work.
export const isFinalCode = (code) =>
  code === "PLAN_DRAFT_NOT_FOUND" ||
  code === "PLAN_DRAFT_NOT_OPEN" ||
  code === "PLAN_VERSION_NOT_FOUND";

// The sentence for a code this file knows, or undefined (an error code shown beside a row).
export const knownMessageForCode = (code) =>
  typeof code === "string" && Object.prototype.hasOwnProperty.call(MESSAGES, code)
    ? MESSAGES[code]
    : undefined;
